import serialize from '@/utils/serialize'
import axios from 'axios'
import Qs from 'qs'
import config from 'SrcConfig'

import { PUBLIC } from '@/constants/api-types'
import {
  LOGOUT,
  REFRESH_TOKEN,
  SET_LOADING_PROGRESS,
  SET_OFFLINE
} from '@/constants/store-types'

export default class ApiCore {
  constructor(store, path, baseUrl = null, headers = null, external = false) {
    this._store = store
    this._path = path || ''
    this._baseUrl = baseUrl || config.apiServerUrl
    this._headers = headers
    this._external = external
  }

  _generateHeaders(refresh = false) {
    const headers = {
      CSN: this._store.getters.fingerprint,
      CVS: this._store.getters.appVersion,
      CTY: this._store.getters.clientType
    }

    const account = this._store.getters.account
    if (account)
      headers.Authorization = `Bearer ${
        refresh ? account.refreshToken : account.accessToken
      }`

    return { ...headers }
  }

  _setProgress(type, loaded, total) {
    const progress =
      loaded !== null && total !== null
        ? parseInt((loaded * 100) / total)
        : 0 || 0
    this._store.commit(SET_LOADING_PROGRESS, { type, progress })
  }

  _setOffline(offline) {
    this._store.commit(SET_OFFLINE, offline)
  }

  _logout() {
    this._store.dispatch(LOGOUT)
  }

  _refreshToken() {
    return this._sendRequest({
      path: `${PUBLIC}/account`,
      action: 'refresh',
      method: 'GET',
      refresh: true
    })
      .then((response) => {
        this._store.dispatch(REFRESH_TOKEN, response)
        return response.accessToken
      })
      .catch((error) => {
        if (error.response.data.error.includes('TOKEN_REFRESH_EXPIRED')) {
          this._logout()
        }
      })
  }

  _sendRequest(o) {
    const request = {
      url: `${this._baseUrl}${o.path?.length ? `/${o.path}` : ''}${
        o.action?.length ? `/${o.action}` : ''
      }`,
      method: o.method,
      params: o.params,
      headers: this._headers || this._generateHeaders(o.refresh),
      paramsSerializer: (params) =>
        Qs.stringify(params, { arrayFormat: 'repeat' }),
      onUploadProgress: (e) => {
        if (o.showProgress) this._setProgress('upload', e.loaded, e.total)
        if (o.onUploadProgress) o.onUploadProgress(e)
      },
      onDownloadProgress: (e) => {
        if (o.showProgress) this._setProgress('download', e.loaded, e.total)
        if (o.onDownloadProgress) o.onDownloadProgress(e)
      }
    }

    if (o.body) request.data = o.body

    if (o.form) {
      if (o.form instanceof FormData) {
        request.data = o.form
      } else {
        Object.keys(o.form).forEach((key) => {
          if (!o.form[key] && o.form[key] !== 0) delete o.form[key]
        })

        request.data = serialize(o.form)
      }
    }

    return axios(request)
      .then((response) => response.data)
      .catch((error) => {
        if (error.message === 'Network Error') {
          this._setOffline(true)
        }

        if (error.response && !this._external) {
          if (error.response.status === 401) {
            this._logout()
          } else if (
            error.response.status === 406 &&
            error.response.data.error.includes('TOKEN_ACCESS_EXPIRED')
          ) {
            return this._refreshToken().then((newAccessToken) => {
              o.accessToken = newAccessToken
              return this._sendRequest(o)
            })
          } else {
            // eslint-disable-next-line no-throw-literal
            throw {
              ...error.response.data,
              status: error.response.status,
              toString: () => error.response.data.description
            }
          }
        } else {
          throw new Error(error)
        }
      })
      .finally(() => {
        if (o.showProgress) {
          this._setProgress('download', null, null)
          this._setProgress('upload', null, null)
        }
      })
  }

  request(o) {
    o.path = this._path
    return this._sendRequest(o)
  }
}
